import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Fade,
  CircularProgress,
  Typography,
  Tooltip,
  Divider
} from '@mui/material';
import { useTheme } from '@mui/material';
import {
  KeyboardArrowDown,
  Send,
  ContentCopy,
  Check,
  SmartToy,
  Close,
  RestartAlt,
  OpenInFull,
  CloseFullscreen,
  SummarizeOutlined,
  QuestionAnswerOutlined,
  EditNoteOutlined
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';

const ChatWindow = ({
  isTranscribed,
  chat,
  chatInput,
  setChatInput,
  chatThinking,
  currentUserMessage,
  messageCopied,
  handleCopyMessage,
  handleSendChatMessage,
  handleSampleQuestion,
  chatContainerRef,
  chatEndRef,
  chatInputRef,
  onResetChat,
  chatResetting = false,
  canResetChat = false,
  onToggleExpand,
  chatExpanded = false,
  canToggleExpand = false,
  onClose,
}) => {
  const theme = useTheme();
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const hasPersistedMessages = Array.isArray(chat) && chat.length > 0;
  const resetTooltipTitle = chatResetting
    ? 'Մաքրում...'
    : !hasPersistedMessages
      ? 'Զրույցը դեռ դատարկ է'
      : !canResetChat
        ? 'Սպասեք ընթացիկ պատասխանին'
        : 'Սկսել նոր զրույց';
  const expandTooltipTitle = !canToggleExpand
    ? 'Հեռախոսում արդեն ամբողջ էկրանով է'
    : chatExpanded
      ? 'Փոքրացնել պատուհանը'
      : 'Մեծացնել պատուհանը';

  useEffect(() => {
    if (chatInputRef.current) {
      chatInputRef.current.focus();
    }
  }, [chatInputRef]);

  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;
    const handleScroll = () => {
      const { scrollTop, clientHeight, scrollHeight } = container;
      if (scrollTop + clientHeight < scrollHeight - 10) {
        setShowScrollIndicator(true);
      } else {
        setShowScrollIndicator(false);
      }
    };
    container.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [chatContainerRef]);

  const renderWaitingState = () => (
    <Box
      sx={{
        mb: 2,
        backgroundColor: 'rgba(0, 0, 0, 0.03)',
        p: 3,
        borderRadius: 2,
        border: '1px dashed rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2
      }}
    >
      <SmartToy sx={{ fontSize: 48, color: theme.palette.primary.main, opacity: 0.8 }} />
      
      <Typography variant="h6" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
        AI օգնական
      </Typography>
      
      <Typography variant="body2" sx={{ textAlign: 'center' }}>
        Ձայնագրությունը դեռ մշակվում է։ Շուտով կարող եք օգտագործել AI օգնականը՝ տեքստի հետ աշխատելու համար։
      </Typography>
      
      <Box sx={{ width: '100%', mt: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <SummarizeOutlined sx={{ mr: 1.5, color: theme.palette.primary.main }} />
          <Typography variant="body2">
            Ստացեք տեքստի ամփոփում և հիմնական մտքերը
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <QuestionAnswerOutlined sx={{ mr: 1.5, color: theme.palette.primary.main }} />
          <Typography variant="body2">
            Հարցեր տվեք տեքստի բովանդակության վերաբերյալ
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <EditNoteOutlined sx={{ mr: 1.5, color: theme.palette.primary.main }} />
          <Typography variant="body2">
            Վերափոխեք տեքստը նամակի, հոդվածի կամ ցանկացած այլ բանի
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  const renderSampleQuestions = () => (
    <Box
      sx={{
        mb: 2,
        backgroundColor: 'rgba(0, 0, 0, 0.03)',
        p: 3,
        borderRadius: 2,
        border: '1px dashed rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        alignItems: 'center'
      }}
    >
      <SmartToy sx={{ fontSize: 48, color: theme.palette.primary.main, opacity: 0.8 }} />
      
      <Typography variant="h6" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
        AI օգնական
      </Typography>
      
      <Typography variant="body2" sx={{ textAlign: 'center', mb: 1 }}>
        Հարցեր տվեք ձեր ձայնագրության վերաբերյալ և ստացեք օգտակար պատասխաններ
      </Typography>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' }}>
        <Button
          variant="outlined"
          size="medium"
          startIcon={<SummarizeOutlined />}
          onClick={() => handleSampleQuestion('Ներկայացրու կետերով հիմնական մտքերը')}
          sx={{ 
            justifyContent: 'flex-start', 
            textTransform: 'none',
            borderRadius: '12px',
            py: 1
          }}
        >
          Ներկայացրու կետերով հիմնական մտքերը
        </Button>
        
        <Button
          variant="outlined"
          size="medium"
          startIcon={<QuestionAnswerOutlined />}
          onClick={() => handleSampleQuestion('Արդյո՞ք ձայնագրությունում նշվում են մարդկանց անուններ')}
          sx={{ 
            justifyContent: 'flex-start', 
            textTransform: 'none',
            borderRadius: '12px',
            py: 1
          }}
        >
          Արդյո՞ք ձայնագրությունում նշվում են մարդկանց անուններ
        </Button>
        
        <Button
          variant="outlined"
          size="medium"
          startIcon={<EditNoteOutlined />}
          onClick={() => handleSampleQuestion('Գրիր արձանագրություն տեքստի հիման վրա իմ ղեկավարի համար')}
          sx={{ 
            justifyContent: 'flex-start', 
            textTransform: 'none',
            borderRadius: '12px',
            py: 1
          }}
        >
          Գրիր արձանագրություն տեքստի հիման վրա իմ ղեկավարի համար
        </Button>
      </Box>
    </Box>
  );

  const renderChatContent = () => (
    <>
      {!isTranscribed ? (
        renderWaitingState()
      ) : (
        <>
          {(!chat || chat.length === 0) && !currentUserMessage && (
            renderSampleQuestions()
          )}
          {chat &&
            chat.map((msg, index) => (
              <Paper
                key={index}
                elevation={1}
                sx={{
                  p: 1.5,
                  borderRadius: '12px',
                  backgroundColor:
                    msg.role === 'user'
                      ? theme.palette.primary.main
                      : theme.palette.background.paper,
                  color: msg.role === 'user' ? 'white' : 'inherit',
                  alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: { xs: '88%', sm: '75%' },
                  width: 'auto',
                  position: 'relative',
                  '&:hover .copy-button': {
                    opacity: 1
                  }
                }}
              >
                {msg.role !== 'user' ? (
                  <Box sx={{ pr: 2 }}>
                    <ReactMarkdown>{msg.content}</ReactMarkdown>
                  </Box>
                ) : (
                  <Typography variant="body2">{msg.content}</Typography>
                )}
                <Tooltip title="Պատճենել" placement="left">
                  <IconButton
                    className="copy-button"
                    size="small"
                    onClick={() => handleCopyMessage(msg.content, index)}
                    sx={{
                      position: 'absolute',
                      bottom: 4,
                      right: 4,
                      opacity: 0,
                      transition: 'opacity 0.2s',
                      color: msg.role === 'user' ? 'white' : 'inherit',
                      backgroundColor:
                        msg.role === 'user'
                          ? 'rgba(255,255,255,0.1)'
                          : 'rgba(0,0,0,0.05)',
                      '&:hover': {
                        backgroundColor:
                          msg.role === 'user'
                            ? 'rgba(255,255,255,0.2)'
                            : 'rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    {messageCopied === index ? (
                      <Check fontSize="small" />
                    ) : (
                      <ContentCopy fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              </Paper>
            ))}
          {currentUserMessage && (
            <Paper
              elevation={1}
              sx={{
                p: 1.5,
                borderRadius: '12px',
                backgroundColor: theme.palette.primary.main,
                color: 'white',
                alignSelf: 'flex-end',
                maxWidth: { xs: '88%', sm: '75%' },
                width: 'auto'
              }}
            >
              <Typography variant="body2">{currentUserMessage}</Typography>
            </Paper>
          )}
          {chatThinking && (
            <Paper
              elevation={1}
              sx={{
                p: 1.5,
                borderRadius: '12px',
                backgroundColor: theme.palette.background.paper,
                alignSelf: 'flex-start',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                maxWidth: { xs: '88%', sm: '75%' },
                width: 'auto'
              }}
            >
              <CircularProgress size={16} thickness={5} />
              <Typography variant="body2">Մտածում եմ...</Typography>
            </Paper>
          )}
        </>
      )}
    </>
  );

  return (
    <Paper
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
      elevation={0}
    >
      <Box
        sx={{
          px: { xs: 1.5, sm: 2 },
          pt: { xs: 'calc(env(safe-area-inset-top) + 10px)', sm: 1.25 },
          pb: { xs: 1, sm: 1.25 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'background.default'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SmartToy sx={{ color: 'primary.main' }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            AI օգնական
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {onToggleExpand && (
            <Tooltip title={expandTooltipTitle}>
              <span>
                <IconButton
                  size="small"
                  onClick={onToggleExpand}
                  aria-label="toggle chat size"
                  disabled={!canToggleExpand}
                >
                  {chatExpanded ? <CloseFullscreen fontSize="small" /> : <OpenInFull fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {onResetChat && (
            <Tooltip title={resetTooltipTitle}>
              <span>
                <IconButton
                  size="small"
                  onClick={onResetChat}
                  aria-label="start new chat"
                  disabled={!canResetChat}
                >
                  {chatResetting ? (
                    <CircularProgress size={16} thickness={5} />
                  ) : (
                    <RestartAlt fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {onClose && (
            <IconButton size="small" onClick={onClose} aria-label="close chat">
              <Close fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>
      <Divider />
      <Box
        ref={chatContainerRef}
        sx={{
          flexGrow: 1,
          minHeight: 0,
          overflowY: 'auto',
          p: { xs: 1.5, sm: 2 },
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
      >
        {renderChatContent()}
        <div ref={chatEndRef} />
        <Fade in={showScrollIndicator}>
          <IconButton
            onClick={() => {
              chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
              setShowScrollIndicator(false);
            }}
            sx={{
              position: 'absolute',
              bottom: 10,
              right: 10,
              backgroundColor: theme.palette.background.paper,
              boxShadow: theme.shadows[2],
              '&:hover': {
                backgroundColor: theme.palette.background.default
              }
            }}
          >
            <KeyboardArrowDown />
          </IconButton>
        </Fade>
      </Box>
      {isTranscribed && (
        <Box
          sx={{
            px: { xs: 1.25, sm: 1.5 },
            pt: 1,
            pb: { xs: 'calc(env(safe-area-inset-bottom) + 10px)', sm: 1.5 },
            borderTop: '1px solid rgba(0, 0, 0, 0.08)',
            position: 'sticky',
            bottom: 0,
            backgroundColor: theme.palette.background.paper,
            zIndex: 1
          }}
        >
          <TextField
            fullWidth
            multiline
            maxRows={4}
            variant="outlined"
            placeholder="Հարց տվեք AI օգնականին..."
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendChatMessage();
              }
            }}
            inputRef={chatInputRef}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleSendChatMessage}
                    color="primary"
                    disabled={!chatInput.trim()}
                    sx={{
                      backgroundColor: chatInput.trim()
                        ? theme.palette.primary.main
                        : 'transparent',
                      color: chatInput.trim()
                        ? 'white'
                        : theme.palette.action.disabled,
                      '&:hover': {
                        backgroundColor: chatInput.trim()
                          ? theme.palette.primary.dark
                          : 'transparent'
                      }
                    }}
                  >
                    <Send />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                borderRadius: '24px',
                backgroundColor:
                  theme.palette.mode === 'light'
                    ? '#f1f3f4'
                    : 'rgba(255, 255, 255, 0.09)',
                '&:hover': {
                  backgroundColor:
                    theme.palette.mode === 'light'
                      ? '#e8eaed'
                      : 'rgba(255, 255, 255, 0.13)'
                },
                boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
              }
            }}
          />
        </Box>
      )}
    </Paper>
  );
};

export default ChatWindow;
